<template>
  <nav class="bottom-tab-bar">
    <!-- Home Tab -->
    <router-link
      :to="homeTo"
      class="bottom-tab-bar__tab"
      :class="{ 'bottom-tab-bar__tab--active': isHomeActive }"
    >
      <div class="bottom-tab-bar__icon-container">
        <v-icon :size="24">{{ isHomeActive ? 'mdi-home' : 'mdi-home-outline' }}</v-icon>
      </div>
      <span class="bottom-tab-bar__label">Home</span>
    </router-link>

    <!-- Search Tab -->
    <router-link
      to="/search"
      class="bottom-tab-bar__tab"
      :class="{ 'bottom-tab-bar__tab--active': isSearchActive }"
    >
      <div class="bottom-tab-bar__icon-container">
        <v-icon :size="24">mdi-magnify</v-icon>
      </div>
      <span class="bottom-tab-bar__label">Search</span>
    </router-link>

    <!-- Center Create Button -->
    <button
      class="bottom-tab-bar__create-btn"
      @click="handleCreateClick"
    >
      <v-icon size="28">mdi-plus</v-icon>
    </button>

    <!-- Notifications Tab -->
    <router-link
      to="/notifications"
      class="bottom-tab-bar__tab"
      :class="{ 'bottom-tab-bar__tab--active': isNotificationsActive }"
    >
      <div class="bottom-tab-bar__icon-container">
        <v-badge
          v-if="unreadNotifications > 0"
          :content="unreadNotifications > 99 ? '99+' : unreadNotifications"
          color="error"
          offset-x="-2"
          offset-y="2"
        >
          <v-icon :size="24">{{ isNotificationsActive ? 'mdi-bell' : 'mdi-bell-outline' }}</v-icon>
        </v-badge>
        <v-icon v-else :size="24">{{ isNotificationsActive ? 'mdi-bell' : 'mdi-bell-outline' }}</v-icon>
      </div>
      <span class="bottom-tab-bar__label">Alerts</span>
    </router-link>

    <!-- Profile Tab -->
    <router-link
      :to="profileTo"
      class="bottom-tab-bar__tab"
      :class="{ 'bottom-tab-bar__tab--active': isProfileActive }"
    >
      <div class="bottom-tab-bar__icon-container">
        <v-icon :size="24">{{ isProfileActive ? 'mdi-account' : 'mdi-account-outline' }}</v-icon>
      </div>
      <span class="bottom-tab-bar__label">Profile</span>
    </router-link>
  </nav>

  <!-- Create Options Bottom Sheet -->
  <Teleport to="body">
    <Transition name="sheet">
      <v-theme-provider v-if="showCreateSheet" :theme="currentTheme" with-background>
        <div
          class="create-sheet-overlay"
          @click="showCreateSheet = false"
        >
          <div class="create-sheet" @click.stop>
            <div class="create-sheet__handle" />
            <div class="create-sheet__options">
              <button class="create-sheet__option" @click="handleCreatePost">
                <div class="create-sheet__option-icon create-sheet__option-icon--post">
                  <v-icon size="24">mdi-pencil-outline</v-icon>
                </div>
                <div class="create-sheet__option-content">
                  <span class="create-sheet__option-title">Create Post</span>
                  <span class="create-sheet__option-desc">Share thoughts, photos, or videos</span>
                </div>
              </button>
              <button v-if="isMusicMode" class="create-sheet__option" @click="handleUploadMusic">
                <div class="create-sheet__option-icon create-sheet__option-icon--music">
                  <v-icon size="24">mdi-music-note-plus</v-icon>
                </div>
                <div class="create-sheet__option-content">
                  <span class="create-sheet__option-title">Upload Song</span>
                  <span class="create-sheet__option-desc">Share your music with the world</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </v-theme-provider>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, inject, nextTick } from "vue";
import { useRoute } from "vue-router";
import { useAuth } from "@/composables/useAuth";
import { useAppSettings } from "@/composables/useAppSettings";
import { useUiStore } from "@/stores/ui";

const route = useRoute();
const { user } = useAuth();
const { isMusicMode } = useAppSettings();
const uiStore = useUiStore();

// Get current theme for teleported content
const currentTheme = computed(() => uiStore.effectiveTheme);

// Inject create functions from App.vue
const openCreatePost = inject<() => void>("openCreatePost");
const openCreateSong = inject<() => void>("openCreateSong");

// Create sheet visibility
const showCreateSheet = ref(false);

// TODO: Add notification badge count from a notification store
const unreadNotifications = computed(() => 0);

// Navigation targets
const homeTo = "/explore";
const profileTo = computed(() =>
  user.value?.username ? `/profile/${user.value.username}` : "/login"
);

// Active state computations
const isHomeActive = computed(() =>
  route.path.startsWith("/explore") ||
  route.path.startsWith("/discover") ||
  route.path.startsWith("/trending") ||
  route.path === "/"
);

const isSearchActive = computed(() => route.path.startsWith("/search"));

const isNotificationsActive = computed(() => route.path.startsWith("/notifications"));

const isProfileActive = computed(() => route.path.startsWith("/profile"));

// Handle create button click
function handleCreateClick() {
  // If music mode is enabled, show the choice sheet
  // Otherwise, just open the post modal directly
  if (isMusicMode.value) {
    showCreateSheet.value = true;
  } else {
    openCreatePost?.();
  }
}

// Handle create post selection
async function handleCreatePost() {
  showCreateSheet.value = false;
  await nextTick();
  openCreatePost?.();
}

// Handle upload music selection
async function handleUploadMusic() {
  showCreateSheet.value = false;
  await nextTick();
  openCreateSong?.();
}
</script>

<style lang="scss" scoped>
.bottom-tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: calc(56px + var(--safe-area-bottom, 0px));
  padding-bottom: var(--safe-area-bottom, 0px);
  background: rgba(var(--v-theme-surface), 0.95);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);

  // Fade gradient above bottom nav for subtle boundary
  &::before {
    content: "";
    position: absolute;
    top: -24px;
    left: 0;
    right: 0;
    height: 24px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(var(--v-theme-surface), 0.4) 50%,
      rgba(var(--v-theme-surface), 0.95) 100%
    );
    pointer-events: none;
  }

  &__tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    height: 56px;
    padding: 6px 0;
    text-decoration: none;
    color: rgba(var(--v-theme-on-surface), 0.6);
    transition: color 0.2s ease;

    &:hover {
      color: rgba(var(--v-theme-on-surface), 0.8);
    }

    &--active {
      color: rgb(var(--v-theme-on-surface)); // White/on-surface for selected state per design

      .bottom-tab-bar__label {
        font-weight: 600;
      }
    }
  }

  &__icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 28px;
  }

  &__label {
    font-size: 10px;
    font-weight: 500;
    margin-top: 2px;
    font-family: var(--font-body);
  }

  &__create-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin: 0 8px;
    border: none;
    border-radius: 50%;
    background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
    color: white;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.3);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(var(--v-theme-primary), 0.4);
    }

    &:active {
      transform: scale(0.95);
    }
  }
}

// Create sheet overlay
.create-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 5000;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

// Create sheet
.create-sheet {
  width: 100%;
  max-width: 500px;
  background: rgb(var(--v-theme-surface));
  border-radius: 20px 20px 0 0;
  padding: 12px 16px calc(16px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.2);

  &__handle {
    width: 36px;
    height: 4px;
    margin: 0 auto 16px;
    background: rgba(var(--v-theme-on-surface), 0.2);
    border-radius: 2px;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 16px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 16px;
    text-align: left;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.1);
    }

    &:active {
      background: rgba(var(--v-theme-on-surface), 0.15);
    }
  }

  &__option-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    flex-shrink: 0;

    &--post {
      background: linear-gradient(135deg, #3b82f6, #1d4ed8);
      color: white;
    }

    &--music {
      background: linear-gradient(135deg, #f59e0b, #d97706);
      color: white;
    }
  }

  &__option-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__option-title {
    font-size: 1rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
  }

  &__option-desc {
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
}

// Sheet transition
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 0.25s ease;

  .create-sheet {
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;

  .create-sheet {
    transform: translateY(100%);
  }
}
</style>
